import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FileText, Printer } from 'lucide-react';
import { useExcomMeetingDetail } from '../../hooks/useApi';
import {
    Avatar,
    Badge,
    Button,
    Card,
    CardBody,
    CardHeader,
    DataTable,
    DetailRow,
    EmptyState,
    ErrorState,
    ListSkeleton,
    PageHeader,
    Skeleton,
    Tabs,
} from '../../components/ui';

const TABS = [
    { value: 'overview', label: 'Overview' },
    { value: 'attendance', label: 'Attendance' },
    { value: 'agenda', label: 'Agenda' },
    { value: 'previous', label: 'Previous actions' },
    { value: 'reports', label: 'Dept. reports' },
    { value: 'discussion', label: 'Discussion' },
    { value: 'decisions', label: 'Decisions' },
    { value: 'newActions', label: 'New actions' },
    { value: 'attachments', label: 'Attachments' },
    { value: 'approvals', label: 'Approvals' },
];

const formatDate = (value) =>
    value
        ? new Date(value).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
        : '—';

const MeetingDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('overview');

    const { data: meeting, isLoading, error } = useExcomMeetingDetail(id);

    if (isLoading) {
        return (
            <>
                <Skeleton w={280} h={26} className="mb-5" />
                <Card>
                    <CardBody>
                        <ListSkeleton rows={5} />
                    </CardBody>
                </Card>
            </>
        );
    }

    if (error || !meeting) {
        return (
            <Card>
                <ErrorState
                    title="Meeting not found"
                    description="This Excom record could not be loaded. It may have been removed."
                    onRetry={() => navigate('/excom')}
                />
            </Card>
        );
    }

    const previousActions = (meeting.actions || []).filter((a) => !a.is_new);
    const newActions = (meeting.actions || []).filter((a) => !!a.is_new);
    const deptReports = Object.keys(meeting.dept_reports || {});

    const actionColumns = [
        {
            key: 'index',
            header: '#',
            width: 48,
            render: (_row, i) => <span className="tabular text-ink-500">{i + 1}</span>,
        },
        {
            key: 'action_point',
            header: 'Action point',
            render: (a) => <span className="text-ink-800">{a.action_point}</span>,
        },
        {
            key: 'owner',
            header: 'Owner',
            render: (a) => (
                <span className="flex items-center gap-2">
                    <Avatar name={a.owner} size="sm" />
                    <span className="text-ink-700 truncate max-w-[130px]">{a.owner}</span>
                </span>
            ),
        },
        {
            key: 'initial_deadline',
            header: 'Deadline',
            align: 'right',
            render: (a) => (
                <span className="tabular text-ink-600 whitespace-nowrap">
                    {formatDate(a.initial_deadline)}
                </span>
            ),
        },
        {
            key: 'status',
            header: 'Status',
            align: 'right',
            render: (a) => <Badge variant="warning">{a.status}</Badge>,
        },
    ];

    const renderTab = () => {
        switch (activeTab) {
            case 'attendance':
                return (
                    <Card>
                        <CardHeader
                            title="Attendance register"
                            subtitle={`${meeting.attendance?.length || 0} members recorded`}
                        />
                        {meeting.attendance?.length ? (
                            <ul className="divide-y divide-[var(--divider-color)]">
                                {meeting.attendance.map((person, i) => (
                                    <li key={i} className="flex items-center gap-3 px-5 py-3">
                                        <Avatar name={`${person.first_name} ${person.last_name}`} size="md" />
                                        <div className="min-w-0 flex-1">
                                            <p className="text-base font-medium text-ink-900 truncate">
                                                {person.first_name} {person.last_name}
                                            </p>
                                            <p className="text-micro text-ink-500 truncate">
                                                {person.role || 'Member'}
                                            </p>
                                        </div>
                                        <Badge variant={person.status === 'present' ? 'success' : 'danger'} dot>
                                            {person.status}
                                        </Badge>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <EmptyState compact title="No attendance recorded" />
                        )}
                    </Card>
                );

            case 'agenda':
                return (
                    <Card>
                        <CardHeader title="Agenda" subtitle={`${meeting.agenda?.length || 0} items`} />
                        {meeting.agenda?.length ? (
                            <ol className="divide-y divide-[var(--divider-color)]">
                                {meeting.agenda.map((item, i) => (
                                    <li key={i} className="flex gap-3 px-5 py-3">
                                        <span className="shrink-0 w-6 h-6 rounded-md bg-ink-100 text-ink-600 text-label font-semibold flex items-center justify-center tabular">
                                            {i + 1}
                                        </span>
                                        <div className="min-w-0">
                                            <p className="text-md font-medium text-ink-900">{item.item_text}</p>
                                            <p className="text-label text-ink-500 mt-0.5">
                                                Presented by {item.presenter || '—'}
                                            </p>
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        ) : (
                            <EmptyState compact title="No agenda recorded" />
                        )}
                    </Card>
                );

            case 'previous':
                return (
                    <Card>
                        <CardHeader title="Previous actions" subtitle="Carried over from earlier meetings" />
                        <DataTable
                            columns={actionColumns}
                            rows={previousActions}
                            rowKey={(_r, i) => i}
                            empty={
                                <EmptyState
                                    compact
                                    title="No previous actions"
                                    description="Nothing was carried over into this meeting."
                                />
                            }
                        />
                    </Card>
                );

            case 'reports':
                return (
                    <Card>
                        <CardHeader
                            title="Departmental reports"
                            subtitle={`${deptReports.length} submitted`}
                        />
                        {deptReports.length ? (
                            <CardBody>
                                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
                                    {deptReports.map((dept) => (
                                        <div
                                            key={dept}
                                            className="flex items-center gap-2 rounded-md border border-[var(--border-color)] px-3 py-2.5"
                                        >
                                            <FileText size={14} className="text-ink-400 shrink-0" aria-hidden="true" />
                                            <span className="text-base font-medium text-ink-900 truncate">{dept}</span>
                                            <Badge variant="success" className="ml-auto">
                                                Submitted
                                            </Badge>
                                        </div>
                                    ))}
                                </div>
                            </CardBody>
                        ) : (
                            <EmptyState
                                compact
                                title="No departmental reports"
                                description="No department submitted a report for this meeting."
                            />
                        )}
                    </Card>
                );

            case 'discussion':
                return (
                    <Card>
                        <CardHeader title="Discussion & AOB" />
                        <CardBody>
                            {meeting.aob ? (
                                <p className="text-md text-ink-700 whitespace-pre-line leading-relaxed max-w-[75ch]">
                                    {meeting.aob}
                                </p>
                            ) : (
                                <p className="text-base text-ink-500">
                                    No discussion points or any-other-business were recorded.
                                </p>
                            )}
                        </CardBody>
                    </Card>
                );

            case 'decisions':
                return (
                    <Card>
                        <CardHeader
                            title="Resolutions"
                            subtitle={`${meeting.decisions?.length || 0} decisions recorded`}
                        />
                        {meeting.decisions?.length ? (
                            <ol className="divide-y divide-[var(--divider-color)]">
                                {meeting.decisions.map((dec, i) => (
                                    <li key={i} className="flex gap-3 px-5 py-3">
                                        <span className="shrink-0 w-6 h-6 rounded-md bg-primary-50 text-primary-600 text-label font-semibold flex items-center justify-center tabular">
                                            {i + 1}
                                        </span>
                                        <p className="text-md text-ink-800 leading-relaxed">{dec.decision_text}</p>
                                    </li>
                                ))}
                            </ol>
                        ) : (
                            <EmptyState compact title="No formal decisions recorded" />
                        )}
                    </Card>
                );

            case 'newActions':
                return (
                    <Card>
                        <CardHeader
                            title="New actions"
                            subtitle={`${newActions.length} assigned in this meeting`}
                        />
                        <DataTable
                            columns={actionColumns.filter((c) => c.key !== 'status')}
                            rows={newActions}
                            rowKey={(_r, i) => i}
                            empty={
                                <EmptyState
                                    compact
                                    title="No new actions"
                                    description="No action items were assigned during this meeting."
                                />
                            }
                        />
                    </Card>
                );

            case 'attachments':
                return (
                    <Card>
                        <CardHeader title="Attachments" />
                        <EmptyState
                            icon={FileText}
                            title="No attachments"
                            description="Supporting documents for this meeting will be listed here."
                        />
                    </Card>
                );

            case 'approvals':
                return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Card>
                            <CardHeader title="Minutes prepared by" />
                            <CardBody>
                                <div className="flex items-center gap-2.5">
                                    <Avatar name={meeting.secretary_name} size="lg" />
                                    <div className="min-w-0">
                                        <p className="text-md font-medium text-ink-900 truncate">
                                            {meeting.secretary_name || '—'}
                                        </p>
                                        <p className="text-label text-ink-500">Secretary</p>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>

                        <Card>
                            <CardHeader
                                title="Minutes approved by"
                                actions={<Badge variant="success">{meeting.status}</Badge>}
                            />
                            <CardBody>
                                <div className="flex items-center gap-2.5">
                                    <Avatar name={meeting.chairperson_name} size="lg" />
                                    <div className="min-w-0">
                                        <p className="text-md font-medium text-ink-900 truncate">
                                            {meeting.chairperson_name || '—'}
                                        </p>
                                        <p className="text-label text-ink-500">Chairperson</p>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                );

            default:
                return (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
                        <Card className="lg:col-span-2">
                            <CardHeader title="Meeting information" />
                            <CardBody className="divide-y divide-[var(--divider-color)] py-1">
                                <DetailRow label="Title">{meeting.title}</DetailRow>
                                <DetailRow label="Reference">
                                    <span className="font-mono">{meeting.ref_no}</span>
                                </DetailRow>
                                <DetailRow label="Date">
                                    <span className="tabular">{formatDate(meeting.meeting_date)}</span>
                                </DetailRow>
                                <DetailRow label="Venue">{meeting.venue}</DetailRow>
                                <DetailRow label="Chairperson">{meeting.chairperson_name}</DetailRow>
                                <DetailRow label="Secretary">{meeting.secretary_name}</DetailRow>
                            </CardBody>
                        </Card>

                        <Card>
                            <CardHeader title="At a glance" />
                            <CardBody>
                                <dl className="space-y-2">
                                    {[
                                        ['Status', <Badge key="s" variant={meeting.status === 'Approved' ? 'success' : 'warning'}>{meeting.status}</Badge>],
                                        ['Agenda items', meeting.agenda?.length || 0],
                                        ['Decisions', meeting.decisions?.length || 0],
                                        ['New actions', newActions.length],
                                        ['Carried-over actions', previousActions.length],
                                    ].map(([label, value]) => (
                                        <div key={label} className="flex items-center justify-between gap-3">
                                            <dt className="text-base text-ink-600">{label}</dt>
                                            <dd className="text-base font-medium text-ink-900 tabular">{value}</dd>
                                        </div>
                                    ))}
                                </dl>
                            </CardBody>
                        </Card>
                    </div>
                );
        }
    };

    return (
        <>
            <PageHeader
                title={meeting.title}
                breadcrumbs={[
                    { label: 'Excom', to: '/excom' },
                    { label: meeting.ref_no || 'Meeting' },
                ]}
                actions={
                    <Button icon={Printer} className="no-print" onClick={() => window.print()}>
                        Print minutes
                    </Button>
                }
            >
                <div className="flex flex-wrap items-center gap-2 mt-2">
                    <Badge variant={meeting.status === 'Approved' ? 'success' : 'warning'}>
                        {meeting.status}
                    </Badge>
                    <span className="text-label font-mono text-ink-500">{meeting.ref_no}</span>
                    <span className="text-ink-300" aria-hidden="true">·</span>
                    <span className="text-label text-ink-600 tabular">
                        {formatDate(meeting.meeting_date)}
                    </span>
                </div>
            </PageHeader>

            <Tabs tabs={TABS} value={activeTab} onChange={setActiveTab} className="mb-4" />

            {renderTab()}
        </>
    );
};

export default MeetingDetail;
